import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext.tsx';
import { apiPost } from '../../lib/api.ts';
import { formatPrice } from '../../lib/utils.ts';
import type { Order } from '@runninglime/shared';
import { LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';

export default function Checkout() {
  const { cart, loading, fetchCart, total } = useCart();
  const navigate = useNavigate();

  const [note, setNote] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const handlePlaceOrder = async () => {
    setPlacing(true);
    setError('');

    const res = await apiPost<Order>('/orders', { note: note || undefined });
    if (res.success && res.data) {
      navigate(`/orders/${res.data.id}`, { replace: true });
    } else {
      setError(res.error || 'Failed to place order');
    }
    setPlacing(false);
  };

  if (loading && !cart) return <LoadingSpinner />;

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <EmptyState
          icon="🛒"
          title="Nothing to checkout"
          description="Your cart is empty"
          action={
            <Link to="/store" className="btn-gradient px-6 py-3 rounded-xl inline-block">
              Browse Store
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <h1 className="text-3xl font-bold text-dark mb-8">Checkout</h1>

      <div className="glass rounded-2xl p-8 shadow-xl animate-slide-up">
        {/* Order Review */}
        <h2 className="font-semibold text-dark text-lg mb-4">Order Review</h2>
        <div className="divide-y divide-gray-100 mb-6">
          {cart.items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-dark text-sm">{item.productName}</p>
                <p className="text-xs text-gray-500">
                  {item.quantity} × {formatPrice(item.pricePerUnit)}
                </p>
              </div>
              <p className="font-semibold text-dark text-sm">
                {formatPrice(item.pricePerUnit * item.quantity)}
              </p>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="mb-6">
          <label htmlFor="note" className="block text-sm font-medium text-dark mb-1.5">
            Order Note (optional)
          </label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white resize-none"
            placeholder="Any special instructions for this order..."
          />
        </div>

        {/* Total */}
        <div className="border-t border-gray-200 pt-4 mb-6">
          <div className="flex justify-between items-baseline">
            <span className="text-lg font-semibold text-dark">Total</span>
            <span className="text-2xl font-bold text-dark">{formatPrice(total)}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Shipping costs will be confirmed separately</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4">
            {error}
          </div>
        )}

        <button
          onClick={handlePlaceOrder}
          disabled={placing}
          className="w-full btn-gradient py-4 rounded-xl text-lg font-semibold disabled:opacity-60 active:scale-[0.98] transition-transform"
        >
          {placing ? 'Placing Order...' : 'Place Order'}
        </button>

        <Link
          to="/cart"
          className="block text-center mt-3 text-sm text-gray-500 hover:text-green transition-colors"
        >
          Back to Cart
        </Link>
      </div>
    </div>
  );
}
